import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { resolveAuthDomain } from './lib/sameOriginAuth';

/**
 * Firebase client setup.
 *
 * ── REPLACE ME WHEN A REAL FIREBASE PROJECT EXISTS ──────────────────────────
 * No production project exists yet. These values are placeholders that satisfy the
 * SDK's shape requirements and are only ever used against the Local Emulator Suite.
 *
 * When the real project is created:
 *   1. Firebase console → Project settings → Your apps → Web app → Config.
 *   2. Copy the values into a `web/.env` file (they are not secrets, but keeping them
 *      out of git makes dev/prod swaps trivial):
 *        VITE_FIREBASE_API_KEY=...
 *        VITE_FIREBASE_AUTH_DOMAIN=...
 *        VITE_FIREBASE_PROJECT_ID=...
 *        VITE_FIREBASE_STORAGE_BUCKET=...
 *        VITE_FIREBASE_MESSAGING_SENDER_ID=...
 *        VITE_FIREBASE_APP_ID=...
 *   3. Nothing else in the app needs to change.
 * ────────────────────────────────────────────────────────────────────────────
 */
const PLACEHOLDER_PROJECT_ID = 'aalrr-new-matter-intake-dev';
const IS_LOCALHOST_BUILD =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

// GitHub Actions secrets can pick up stray leading/trailing whitespace when copy-pasted
// (this has happened before — a trailing space on the API key silently broke Auth even
// though the key value itself was otherwise correct). Trim defensively on every value.
const trimEnv = (v) => (typeof v === 'string' ? v.trim() : v);

/**
 * The auth domain, which may be this host rather than the project's.
 *
 * See lib/sameOriginAuth.js: `signInWithRedirect` needs its handler on the same origin as the
 * app, or the session cannot be handed back in a browser that blocks cross-site storage. It is
 * opt-in per browser until proved, because a wrong value locks everybody out.
 */
const configuredAuthDomain =
  trimEnv(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN) || `${PLACEHOLDER_PROJECT_ID}.firebaseapp.com`;

const { authDomain: resolvedAuthDomain, sameOrigin: SAME_ORIGIN_AUTH } =
  typeof window === 'undefined'
    ? { authDomain: configuredAuthDomain, sameOrigin: false }
    : resolveAuthDomain({
        hostname: window.location.hostname,
        search: window.location.search,
        fallback: configuredAuthDomain,
      });

/** Whether redirect sign-in can work here, which is what makes an automatic attempt worth making. */
export const SAME_ORIGIN_AUTH_ENABLED = SAME_ORIGIN_AUTH;

const firebaseConfig = {
  apiKey: trimEnv(import.meta.env.VITE_FIREBASE_API_KEY) || 'demo-emulator-api-key',
  authDomain: resolvedAuthDomain,
  projectId: trimEnv(import.meta.env.VITE_FIREBASE_PROJECT_ID) || PLACEHOLDER_PROJECT_ID,
  storageBucket:
    trimEnv(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET) || `${PLACEHOLDER_PROJECT_ID}.appspot.com`,
  messagingSenderId: trimEnv(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID) || '000000000000',
  appId:
    trimEnv(import.meta.env.VITE_FIREBASE_APP_ID) || '1:000000000000:web:0000000000000000000000',
};

// Fail loudly instead of silently shipping placeholder/emulator credentials to a real
// domain. A real Firebase API key always starts with "AIzaSy"; catches both a genuinely
// missing env var AND a misconfigured secret (e.g. one holding the wrong file's contents).
if (!IS_LOCALHOST_BUILD && !/^AIzaSy[A-Za-z0-9_-]{20,}$/.test(firebaseConfig.apiKey)) {
  throw new Error(
    '[firebase] VITE_FIREBASE_API_KEY is missing or malformed for a production build. ' +
      'Refusing to initialize Firebase with placeholder/invalid credentials. ' +
      'Check the VITE_FIREBASE_* GitHub Actions secrets — each must hold the single ' +
      'corresponding value from Firebase console → Project settings → Your apps → Config, ' +
      'not the service account JSON key file.'
  );
}

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

/**
 * Emulator wiring. We connect whenever the page is served from localhost, which covers
 * both `vite dev` (5173) and the Hosting emulator (5000). A deployed build served from
 * a real domain never takes this branch.
 */
const isLocalhost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

export const USING_EMULATORS = isLocalhost && import.meta.env.VITE_USE_EMULATORS !== 'false';

if (USING_EMULATORS) {
  const host = window.location.hostname === 'localhost' ? 'localhost' : '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(functions, host, 5001);
  connectStorageEmulator(storage, host, 9199);
  // eslint-disable-next-line no-console
  console.info('[firebase] connected to the Local Emulator Suite');
}

/** Base URL for the HTTP API (magic-link approval endpoints). */
export const API_BASE = USING_EMULATORS
  ? `http://${window.location.hostname}:5001/${firebaseConfig.projectId}/us-central1/api`
  : '/api';
